

import React, { useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import SelectColumnFilter from '../ReactTable/SelectColumnFilter';
import { useHistory } from 'react-router';
import { COLORS } from '../../config';
import SecondaryButton from '../../components/Buttons/SecondaryButton';
import SecondaryButtonAccent from '../../components/Buttons/SecondaryButtonAccent';
import PlusIcon from '../../components/Icons/solid/PlusIcon';
import AdjustmentsIcon from '../../components/Icons/solid/AdjustmentsIcon';
import DotsIcon from '../../components/Icons/solid/DotsIcon';
import GlobalFilter from '../ReactTable/GlobalFilter';
import TablePagination from '../ReactTable/TablePagination';
import FiltersModal from '../ReactTable/FiltersModal';
import SortAscSmall from '../../components/Icons/solid/SortAscSmall';
import SortDescSmall from '../../components/Icons/solid/SortDescSmall';
import MoonLoader from 'react-spinners/MoonLoader'
import TableRowSubMenu from '../../components/Menus/TableRowSubMenu';
import useInvoices from '../../store/invoices/invoicesContext';
import DocSearchItem from '../../components/Icons/outline/DocSearchIcon';
import DeleteInvoiceModal from './DeleteInvoiceModal';
import DownloadIcon from '../../components/Icons/outline/DownloadIcon';
import useApp from '../../store/app/appContext';
import { TrashIcon } from '@heroicons/react/outline';
import useOnClickOutside from '../../hooks/useOnClickOutside';

function InvoiceList() {

  const ref = useRef();
  
  const {
    appState: { user, userAccount },
  } = useApp();

  const { getAllInvoices, getUserInvoices, downloadInvoice, deleteInvoice } = useInvoices();
  
  const history = useHistory();

  const [isLoading, setIsLoading] = React.useState(true);
  const [showDeleteInvoiceModal, setShowDeleteInvoiceModal] = React.useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = React.useState(null);
  const [allInvoices, setAllInvoices] = React.useState([]);
  const [showSubMenu, setShowSubMenu] = React.useState({
    active: false,
    row: null
  })

  React.useEffect(() => {
    if (userAccount.isAdmin) {
      getAllInvoices().then((res) => {
        const invoices = res;
        setIsLoading(false)
        setAllInvoices(invoices);
      });
    }
    if (userAccount.isPartner) {
      getUserInvoices(user._id).then((res) => {
        const invoices = res;
        setIsLoading(false)
        setAllInvoices(invoices);
      });
    }
  }, [userAccount])

  useOnClickOutside(
    ref,
    setShowSubMenu.bind(this, {
      active: false,
      row: null,
    })
  );

  const [showFilters, setShowFilters] = React.useState(false)

  const data = React.useMemo(
    () => []
  
  , [allInvoices]);

  if (allInvoices && allInvoices.length && !data.length) {
    allInvoices.forEach(invoice => {
      const currentStatus = invoice.status.slice(-1)[0].status
      data.push({
        id: invoice._id,
        fullName: invoice?.user_id?.first_name + ' ' + invoice?.user_id?.last_name || '',
        uploaded: dayjs(invoice.created_at).format('DD/MM/YYYY'),
        amount: invoice.amount,
        status: currentStatus,
        fileName: invoice.file_name
      })
    })
  }

  const handleRowSubMenuClick = (row) => {
    setShowSubMenu({
      active: !showSubMenu.active,
      row: row
    })
  }

  const handleViewDetails = (rowIndex) => {
    
    if (userAccount.isAdmin) history.push('/manage-invoices/' + allInvoices[rowIndex]._id)
    if (userAccount.isPartner) history.push('/my-invoices/' + allInvoices[rowIndex]._id)
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  const defaultColumn = React.useMemo(() => {
    return {
      Filter: SelectColumnFilter
    }
  }, [])

  const handleRemoveInvoice = (invoiceInfo) => {
    setInvoiceToDelete(invoiceInfo);
    setShowDeleteInvoiceModal(true);
  }

  const handleDeleteInvoice = (invoiceInfo) => {
    deleteInvoice(invoiceInfo.id).then(res => {
      const updatedArray = allInvoices.filter(invoice => invoice._id !== invoiceInfo.id);
      setAllInvoices(updatedArray);
    })
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  const handleDownloadInvoice = (InvoiceInfo) => {
    downloadInvoice(InvoiceInfo.id, InvoiceInfo.fileName)
  }

  const columns = React.useMemo(
    () => [
      {
        Header: 'User',
        accessor: 'fullName',
        disableFilters: true,
      },
      {
        Header: 'Invoice',
        accessor: 'fileName',
        disableFilters: true,
      },
      {
        Header: 'Uploaded',
        accessor: 'uploaded',
      },
      {
        Header: 'Amount',
        accessor: 'amount',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
    ], []
  );

  const { 
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    gotoPage,
    prepareRow,
    state : { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters
  } = useTable(
    {
      columns,
      data,
      defaultColumn
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );


  return (
    <div className='w-full rounded bg-white shadow'>
      <FiltersModal
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        headerGroups={headerGroups}
        setAllFilters={setAllFilters}
      />
      {userAccount.isAdmin && <DeleteInvoiceModal 
        showDeleteInvoiceModal={showDeleteInvoiceModal}
        setShowDeleteInvoiceModal={setShowDeleteInvoiceModal}
        onDelete={() => handleDeleteInvoice(invoiceToDelete)}
        forceDelete={invoiceToDelete?.deleted ? true : false}
      />}
      <div className='p-2 xl:p-4 grid grid-cols-1 gap-y-2 lg:flex lg:items-center w-full'>
        {userAccount.isPartner && <SecondaryButtonAccent text="Add invoice" onClick={() => history.push('/my-invoices/add')} icon={<PlusIcon fill={COLORS.colorPrimary}/>}/>}
        <SecondaryButton text="Set filters" onClick={() => setShowFilters(true)} className='lg:ml-auto lg:mr-2' icon={<AdjustmentsIcon fill={COLORS.colorSecondary}/>}/>
        <div>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      </div>
      {isLoading && 
      <div className='flex justify-around min-h-170 pt-8'>
        <MoonLoader color={COLORS.colorPrimary}/>
      </div> 
      }
      {!isLoading &&
      <div className='overflow-x-scroll'>
        <table {...getTableProps()} className='w-full mt-4 border-8 border-transparent'>
          <thead >
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '/>
                {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold'>
                  <span className='flex items-center'>
                  {column.render('Header')}
                  {column.isSorted ? 
                    column.isSortedDesc ? 
                      <SortAscSmall style={'ml-1 h-5 w-5'}/>
                      : <SortDescSmall style={'ml-1 h-5 w-5'} />
                  : ''}
                  </span>
                </th>
                ))}
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '></th>  
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className={`border-b-2 border-gray-100`}>
                  <td  className='text-secondary font-medium xl:px-2 py-3'/>
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                  <td className='text-left xl:px-2 relative'>
                    <div ref={ref} className='cursor-pointer flex flex-row-reverse '>
                      <DotsIcon vertical fill={COLORS.colorShade} onClick={() => handleRowSubMenuClick(row.index)}/>
                    </div>
                    {showSubMenu.active && showSubMenu.row === row.index &&
                    <TableRowSubMenu>
                      <ul className='whitespace-nowrap z-50' style={{ zIndex: '1000'}}>
                        <li className='cursor-pointer flex items-center' onClick={() => handleViewDetails(row.index)}>
                          <DocSearchItem size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>View invoice details</span>
                        </li>
                        <li className='cursor-pointer flex items-center mt-2' onClick={() => handleDownloadInvoice(row.original)}>
                          <DownloadIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>{'Download Invoice'}</span>
                        </li>
                        {userAccount.isAdmin &&  <li className='cursor-pointer flex items-center mt-2' onClick={() => handleRemoveInvoice(row.original)}>
                          <TrashIcon className='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>{'Delete Invoice'}</span>
                        </li> }
                      </ul>
                    </TableRowSubMenu>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      }
      <TablePagination   
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        setPageSize={setPageSize}
        pageSize={pageSize}
        gotoPage={gotoPage}
      />
    </div>
  )
}

export default InvoiceList